import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const UserReviews = ({ reviews, averageRating, totalReviews }) => {
  const [sortBy, setSortBy] = useState('newest');
  const [filterRating, setFilterRating] = useState('all');

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const reviewDate = new Date(date);
    const diffInDays = Math.floor((now - reviewDate) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 30) return `${diffInDays} days ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return `${Math.floor(diffInDays / 365)} years ago`;
  };

  // Filter and sort reviews based on user selections
  const getFilteredAndSortedReviews = () => {
    let filtered = [...reviews];

    // Apply rating filter
    if (filterRating !== 'all') {
      const targetRating = parseInt(filterRating);
      filtered = filtered?.filter(r => Math.floor(r?.rating) === targetRating);
    }

    // Apply sorting
    switch (sortBy) {
      case 'newest':
        filtered?.sort((a, b) => new Date(b?.createdAt) - new Date(a?.createdAt));
        break;
      case 'oldest':
        filtered?.sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt));
        break;
      case 'highest':
        filtered?.sort((a, b) => b?.rating - a?.rating);
        break;
      case 'lowest':
        filtered?.sort((a, b) => a?.rating - b?.rating);
        break;
      case 'helpful':
        filtered?.sort((a, b) => (b?.helpfulCount || 0) - (a?.helpfulCount || 0));
        break;
      default:
        break;
    }

    return filtered;
  };

  const displayReviews = getFilteredAndSortedReviews();

  return (
    <div className="bg-white rounded-xl shadow-subtle border border-border p-6 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
          <Icon name="MessageSquare" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-text-primary">User Reviews</h2>
          <p className="text-sm text-muted-foreground">
            {totalReviews} verified purchase reviews
          </p>
        </div>
      </div>
      {/* Rating Summary */}
      <div className="bg-muted/30 rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
              <span className="text-4xl font-bold text-primary">{averageRating}</span>
              <div className="flex items-center">
                {[...Array(5)]?.map((_, i) => (
                  <Icon
                    key={i}
                    name="Star"
                    size={20}
                    className={`${
                      i < Math.floor(averageRating)
                        ? 'text-accent fill-current' :'text-muted-foreground'
                    }`}
                  />
                ))}
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Based on {totalReviews} verified reviews
            </p>
          </div>

          <div className="space-y-2">
            {[5, 4, 3, 2, 1]?.map((rating) => {
              const count = reviews?.filter(r => Math.floor(r?.rating) === rating)?.length;
              const percentage = (count / totalReviews) * 100;
              
              return (
                <div key={rating} className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground w-8">
                    {rating}★
                  </span>
                  <div className="flex-1 bg-muted rounded-full h-2">
                    <div
                      className="bg-accent h-2 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-8">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text-primary">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-1 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rated</option>
            <option value="lowest">Lowest Rated</option>
            <option value="helpful">Most Helpful</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text-primary">Filter:</span>
          <select
            value={filterRating}
            onChange={(e) => setFilterRating(e?.target?.value)}
            className="px-3 py-1 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
        </div>
      </div>
      {/* Reviews List */}
      <div className="space-y-6">
        {displayReviews?.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No reviews match your filters</p>
          </div>
        ) : (
          displayReviews?.slice(0, 5)?.map((review, index) => (
            <div key={review?.id || index} className="border-b border-border pb-6 last:border-b-0 last:pb-0">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Image
                    src={review?.avatarUrl}
                    alt={review?.avatarAlt}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                </div>

                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-text-primary">{review?.reviewerName}</h4>
                        {review?.isVerified && (
                          <div className="flex items-center gap-1 bg-success/10 text-success px-2 py-0.5 rounded-full text-xs">
                            <Icon name="ShieldCheck" size={12} />
                            Verified Purchase
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center">
                          {[...Array(5)]?.map((_, i) => (
                            <Icon
                              key={i}
                              name="Star"
                              size={14}
                              className={`${
                                i < Math.floor(review?.rating)
                                  ? 'text-accent fill-current' :'text-muted-foreground'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {getTimeAgo(review?.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="font-medium text-text-primary mb-2">{review?.title}</h5>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {review?.content}
                    </p>
                  </div>

                  {review?.reviewImages && review?.reviewImages?.length > 0 && (
                    <div className="flex gap-2">
                      {review?.reviewImages?.map((img, imgIndex) => (
                        <div key={img?.id || imgIndex} className="w-16 h-16 rounded-lg overflow-hidden">
                          <Image
                            src={img?.imageUrl}
                            alt={img?.imageAlt}
                            className="w-full h-full object-cover hover:scale-110 transition-transform duration-200 cursor-pointer"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-4 pt-2">
                    <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors">
                      <Icon name="ThumbsUp" size={14} />
                      Helpful ({review?.helpfulCount || 0})
                    </button>
                    <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors">
                      <Icon name="MessageCircle" size={14} />
                      Reply
                    </button>
                    <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors">
                      <Icon name="Flag" size={14} />
                      Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="mt-6 text-center">
        <Button variant="outline" iconName="ChevronDown" iconPosition="right">
          Load More Reviews
        </Button>
      </div>
    </div>
  );
};

export default UserReviews;